import { MobXProviderContext } from "mobx-react";
import { Button } from "primereact/button";
import { confirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { RouterAddress } from "../../../constants/RouterAddress";
import { getErrorTitle, USER_GROUP_ADMIN } from "../../../constants/constants";
import { ROLE_TYPE_ADMIN, ROLE_TYPE_PROVIDER } from "../../../constants/RegisterConstants";
import {
    appFormSectionStyle,
    appDialogContentStyle,
    appDialogHeaderStyle,
    appDialogStyle,
    appMutedTextStyle,
    appNeutralPanelStyle,
    appSectionCardStyle,
    appConfirmDialogClasses,
    renderConfirmMessage,
    renderSurfaceHeader,
} from "../../../components/general/uiSurfaceStyles";
import { dashboardPageContainerStyle, renderDashboardHeader } from "../../dashboard/pages/dashboardStyles";
import Unauthorized from "../../../shared/pages/Unauthorized";
import { NurseResidentAssessmentPage } from "./NurseResidentAssessmentPage";
import { openUrlInNewTab } from "../../../utils/securityUtils";
import {
    ensureResidentBinderBundle,
    getBinderFolderByCode,
    RESIDENT_BINDER_FOLDER_CODES,
    upsertBinderDocumentWithVersion,
} from "../../../utils/binderFeatureUtils";

const ALLOWED_EXTENSIONS = ["pdf", "doc", "docx", "txt", "png", "jpg", "jpeg", "tif", "tiff", "mp3", "wav", "m4a"];
const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;
const MAX_STAGED_FILES = 10;
const DOC_TAG_OPTIONS = [
    { label: "Face Sheet", value: "FaceSheet" },
    { label: "MAR Sheet", value: "MARSheet" },
    { label: "Assessment Notes", value: "AssessmentNotes" },
];

const getAssessmentStatusLabel = (status: string) => {
    const normalized = String(status || "draft").trim().toLowerCase();
    if (normalized === "completed") return "Completed";
    if (normalized === "in_progress" || normalized === "in progress") return "In Progress";
    if (normalized === "draft") return "Draft";
    return normalized ? normalized.charAt(0).toUpperCase() + normalized.slice(1) : "Draft";
};

const getAssessmentStatusTagSeverity = (status: string): "success" | "info" | "warning" | "danger" | "secondary" | undefined => {
    const normalized = String(status || "draft").trim().toLowerCase();
    if (normalized === "completed") return "success";
    if (normalized === "in_progress" || normalized === "in progress") return "info";
    if (normalized === "draft") return "warning";
    return "secondary";
};

type StagedFile = {
    id: string;
    file: File;
    tags: string[];
};

const getVersionSortTime = (form: any) => {
    const raw = form?.modifiedAt || form?.createdAt;
    const parsed = raw ? Date.parse(String(raw)) : NaN;
    return Number.isNaN(parsed) ? 0 : parsed;
};

const formatVersionUpdatedAt = (form: any) => {
    const raw = form?.modifiedAt || form?.createdAt;
    const parsed = raw ? Date.parse(String(raw)) : NaN;
    if (Number.isNaN(parsed)) return "Date unavailable";
    return new Date(parsed).toLocaleString();
};

const startDraftDialogStyle = {
    ...appDialogStyle,
    width: "min(96vw, 58rem)",
};

const startDraftDialogContentStyle = {
    ...appDialogContentStyle,
    maxHeight: "80vh",
    overflowY: "auto" as const,
};

const ProviderResidentAssessmentPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { homeId, residentId } = useParams();
    const { authStore, backendAPIStore } = useContext(MobXProviderContext);
    const toast = useRef<Toast>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [generatingAssessmentDocument, setGeneratingAssessmentDocument] = useState(false);
    const [downloadingMarSheet, setDownloadingMarSheet] = useState(false);
    const [residentProfile, setResidentProfile] = useState<any>(null);
    const [assessmentForms, setAssessmentForms] = useState<any[]>([]);
    const [showNewDraftDialog, setShowNewDraftDialog] = useState(false);
    const [showAssessmentHistoryDialog, setShowAssessmentHistoryDialog] = useState(false);
    const [showFeatureAccessDialog, setShowFeatureAccessDialog] = useState(false);
    const [featureAccessDialogMessage, setFeatureAccessDialogMessage] = useState("");
    const [featureAccessDialogAvailableCredits, setFeatureAccessDialogAvailableCredits] = useState(0);
    const [featureAccessDialogAllowUseCredit, setFeatureAccessDialogAllowUseCredit] = useState(false);
    const [automationWizardStep, setAutomationWizardStep] = useState<"choose" | "files">("choose");
    const [stagedFiles, setStagedFiles] = useState<StagedFile[]>([]);
    const [tagValidationRequested, setTagValidationRequested] = useState(false);
    const [creatingManualVersion, setCreatingManualVersion] = useState(false);
    const featureAccessDialogUseCreditActionRef = useRef<null | (() => void)>(null);

    const userRoleType = authStore.getUserRoleType();
    const userGroups = authStore.getUserGroups() || [];
    const isAdminUser = userGroups.includes(USER_GROUP_ADMIN) || userRoleType === ROLE_TYPE_ADMIN;
    const isSharedResidentMode = location.pathname.startsWith("/shared/home/") && location.pathname.includes("/assessment/");
    const requiresProviderSubscriptionMessageOnly = isSharedResidentMode;

    const latestAssessmentForm = useMemo(() => {
        return assessmentForms
            .slice()
            .sort((left: any, right: any) => getVersionSortTime(right) - getVersionSortTime(left))[0] || null;
    }, [assessmentForms]);

    const assessmentPagePath = useMemo(() => {
        return isSharedResidentMode
            ? RouterAddress.getSharedResidentAssessmentPagePath(homeId || "", residentId || "")
            : RouterAddress.getProviderResidentAssessmentPagePath(homeId || "", residentId || "");
    }, [homeId, isSharedResidentMode, residentId]);
    const residentBinderPath = useMemo(() => (
        isSharedResidentMode
            ? RouterAddress.getSharedResidentBinderPagePath(homeId || "", residentId || "")
            : RouterAddress.getResidentBinderPagePath(homeId || "", residentId || "")
    ), [homeId, isSharedResidentMode, residentId]);
    const activeFormId = useMemo(() => String(new URLSearchParams(location.search).get("formId") || "").trim(), [location.search]);
    const activeAssessmentForm = useMemo(() => {
        if (!activeFormId) return latestAssessmentForm;
        return assessmentForms.find((f: any) => String(f?.formId || "") === activeFormId) || latestAssessmentForm;
    }, [activeFormId, assessmentForms, latestAssessmentForm]);
    const resolvedFormId = useMemo(() => {
        if (activeFormId) return activeFormId;
        return String(latestAssessmentForm?.formId || "").trim();
    }, [activeFormId, latestAssessmentForm?.formId]);

    const openAssessmentInline = (formId: string, step?: number, processingJobId?: string) => {
        const normalizedFormId = String(formId || "").trim();
        if (!normalizedFormId) return;
        const query = new URLSearchParams();
        query.set("formId", normalizedFormId);
        if (step !== undefined) {
            query.set("step", String(step));
        }
        if (processingJobId) {
            query.set("showProcessing", "1");
            query.set("jobId", processingJobId);
        }
        const nextPath = `${assessmentPagePath}?${query.toString()}`;
        navigate(nextPath);
    };

    const loadSummary = useCallback(async () => {
        if (!residentId || !homeId) return;
        setLoading(true);
        try {
            await backendAPIStore.init();
            const [profileResponse, formsResponse] = await Promise.all([
                backendAPIStore.getResidentDetails(residentId),
                backendAPIStore.getProviderAssessmentForms(residentId),
            ]);
            setResidentProfile(profileResponse || null);
            setAssessmentForms(Array.isArray(formsResponse?.forms) ? formsResponse.forms : (Array.isArray(formsResponse) ? formsResponse : []));
        } catch (error: any) {
            toast.current?.show({
                severity: "error",
                summary: getErrorTitle(error),
                detail: error?.message || "Unable to load assessment workspace.",
                life: 3500,
            });
        } finally {
            setLoading(false);
        }
    }, [backendAPIStore, homeId, residentId]);

    useEffect(() => {
        void loadSummary();
    }, [loadSummary]);

    useEffect(() => {
        if (!homeId || !residentId) return;
        if (activeFormId) return;
        const fallbackFormId = String(latestAssessmentForm?.formId || "").trim();
        if (!fallbackFormId) return;
        navigate(`${assessmentPagePath}?formId=${encodeURIComponent(fallbackFormId)}`, { replace: true });
    }, [activeFormId, assessmentPagePath, homeId, latestAssessmentForm?.formId, navigate, residentId]);

    const handleFileSelect = (event: any) => {
        const files = Array.from<File>(event.target.files || []);
        const valid: StagedFile[] = [];
        const invalid: string[] = [];
        const remainingSlots = Math.max(0, MAX_STAGED_FILES - stagedFiles.length);
        const overflowFiles: string[] = [];

        files.forEach((file: File, index: number) => {
            const ext = file.name.split(".").pop()?.toLowerCase() || "";
            if (!ALLOWED_EXTENSIONS.includes(ext) || file.size > MAX_FILE_SIZE_BYTES) {
                invalid.push(file.name);
            } else if (index >= remainingSlots) {
                overflowFiles.push(file.name);
            } else {
                valid.push({ id: uuidv4(), file, tags: [] });
            }
        });

        if (invalid.length) {
            toast.current?.show({
                severity: "warn",
                summary: "Skipped files",
                detail: `Unsupported or too large: ${invalid.join(", ")}`,
                life: 4500,
            });
        }

        if (overflowFiles.length) {
            toast.current?.show({
                severity: "warn",
                summary: "Document limit reached",
                detail: `You can add up to ${MAX_STAGED_FILES} documents. Skipped: ${overflowFiles.join(", ")}`,
                life: 4500,
            });
        }

        setTagValidationRequested(false);
        setStagedFiles((prev) => [...prev, ...valid]);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const toggleStagedTag = (id: string, tag: string) => {
        setTagValidationRequested(false);
        setStagedFiles((prev) => prev.map((file) => {
            if (file.id !== id) return file;
            const hasTag = (file.tags || []).includes(tag);
            return {
                ...file,
                tags: hasTag
                    ? file.tags.filter((currentTag) => currentTag !== tag)
                    : [...(file.tags || []), tag],
            };
        }));
    };

    const removeStagedFile = (id: string) => {
        setStagedFiles((prev) => prev.filter((file) => file.id !== id));
    };

    const openProviderAssessmentCreditDialog = useCallback((error?: any, options?: { allowUseCredit?: boolean; onUseCredit?: () => void; }) => {
        if (requiresProviderSubscriptionMessageOnly) {
            setFeatureAccessDialogAvailableCredits(0);
            setFeatureAccessDialogAllowUseCredit(false);
            featureAccessDialogUseCreditActionRef.current = null;
            setFeatureAccessDialogMessage("The provider must have an active subscription to use the shared resident assessment workspace.");
            setShowFeatureAccessDialog(true);
            return;
        }
        const availableCredits = Number(
            error?.automationCreditsAvailable
            || error?.details?.automationCreditsAvailable
            || 0
        );
        const canUseCredit = Boolean(options?.allowUseCredit && availableCredits > 0);
        setFeatureAccessDialogAvailableCredits(availableCredits);
        setFeatureAccessDialogAllowUseCredit(canUseCredit);
        featureAccessDialogUseCreditActionRef.current = canUseCredit && options?.onUseCredit ? options.onUseCredit : null;
        setFeatureAccessDialogMessage(
            availableCredits > 0
                ? "Your monthly draft uses are used up. Use 1 automation credit to continue."
                : (String(error?.message || error?.details?.error || "").trim()
                    || "No automation credits are available for provider drafting. Buy Automation Credits to continue.")
        );
        setShowFeatureAccessDialog(true);
    }, [requiresProviderSubscriptionMessageOnly]);

    const startDraftAutomation = async (useExistingCredit: boolean = false) => {
        if (!residentId || !homeId || submitting || stagedFiles.length === 0) return;
        if (stagedFiles.some((file) => file.tags.length === 0)) {
            setTagValidationRequested(true);
            return;
        }

        setSubmitting(true);
        try {
            await backendAPIStore.init();
            const uploadedDocs: Array<{ objectKey: string; documentId: string; fileName: string; tag: string; tags: string[] }> = [];

            for (const staged of stagedFiles) {
                const documentId = uuidv4();
                const ext = staged.file.name.split(".").pop()?.toLowerCase() || "pdf";
                const { presignedUrl, objectKey } = await backendAPIStore.getProviderAssessmentPresignedUrl(residentId, documentId, ext);
                await backendAPIStore.uploadFileToS3(presignedUrl, staged.file);
                uploadedDocs.push({
                    objectKey,
                    documentId,
                    fileName: staged.file.name,
                    tag: staged.tags[0] || "AssessmentNotes",
                    tags: staged.tags,
                });
            }

            const createdForm = await backendAPIStore.createProviderAssessmentForm(residentId);
            const nextFormId = String(createdForm?.formId || "").trim();
            const jobResult = await backendAPIStore.createProviderResidentAssessmentJob({
                residentId,
                formId: nextFormId || undefined,
                documents: uploadedDocs,
                generateDelegationDrafts: false,
                billingAccessWorkspace: "provider_resident",
                useExistingCredit,
            });
            const nextJobId = String(jobResult?.jobId || "").trim();

            toast.current?.show({
                severity: "success",
                summary: "Draft started",
                detail: "Assessment draft automation has started.",
                life: 3000,
            });

            setShowNewDraftDialog(false);
            setAutomationWizardStep("files");
            setStagedFiles([]);
            await loadSummary();
            if (nextFormId) {
                openAssessmentInline(nextFormId, undefined, nextJobId || undefined);
            }
        } catch (error: any) {
            const code = String(error?.code || error?.details?.code || "").trim().toUpperCase();
            if (code === "SUBSCRIPTION_LIMIT_REACHED") {
                openProviderAssessmentCreditDialog(error, {
                    allowUseCredit: !useExistingCredit,
                    onUseCredit: () => { void startDraftAutomation(true); },
                });
            } else {
                toast.current?.show({
                    severity: "error",
                    summary: getErrorTitle(error),
                    detail: error?.message || "Unable to start draft automation.",
                    life: 4000,
                });
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleStartNewAssessment = () => {
        setAutomationWizardStep("files");
        setShowNewDraftDialog(true);
    };

    const handleCreateManualVersion = async () => {
        if (!residentId || creatingManualVersion) return;
        setCreatingManualVersion(true);
        try {
            await backendAPIStore.init();
            const createdForm = await backendAPIStore.createProviderAssessmentForm(residentId);
            const nextFormId = String(createdForm?.formId || "").trim();
            if (!nextFormId) {
                throw new Error("Unable to create a new assessment version right now.");
            }

            toast.current?.show({
                severity: "success",
                summary: "New version created",
                detail: "A fresh assessment version is ready to edit.",
                life: 3000,
            });
            await loadSummary();
            openAssessmentInline(nextFormId);
        } catch (error: any) {
            toast.current?.show({
                severity: "error",
                summary: getErrorTitle(error),
                detail: error?.message || "Unable to create a new assessment version right now.",
                life: 4000,
            });
        } finally {
            setCreatingManualVersion(false);
        }
    };

    const handleMarkAssessmentComplete = async () => {
        if (!latestAssessmentForm?.formId) {
            toast.current?.show({ severity: "warn", summary: "No assessment", detail: "Create or open an assessment first.", life: 3000 });
            return;
        }
        try {
            await backendAPIStore.init();
            await backendAPIStore.updateProviderAssessmentForm({ formId: latestAssessmentForm.formId, status: "completed" });
            toast.current?.show({ severity: "success", summary: "Assessment completed", detail: "This assessment has been marked as completed.", life: 3000 });
            await loadSummary();
        } catch (error: any) {
            toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to mark assessment complete.", life: 3500 });
        }
    };

    const saveAssessmentDocumentToBinder = async (response: any) => {
        if (!residentId) return;

        const outputKey = String(response?.outputKey || "").trim();
        const fileName = String(response?.fileName || "").trim();
        if (!outputKey || !fileName) {
            throw new Error("Assessment document is missing binder save details.");
        }

        const bundle = await ensureResidentBinderBundle(backendAPIStore, residentId);
        const targetFolder = getBinderFolderByCode(bundle.folders, RESIDENT_BINDER_FOLDER_CODES.ASSESSMENT);
        if (!bundle.binder?.binderId || !targetFolder?.folderId) {
            throw new Error("Resident assessment binder folder is not available.");
        }

        await upsertBinderDocumentWithVersion({
            backendAPIStore,
            binderId: bundle.binder.binderId,
            folderId: targetFolder.folderId,
            folderName: targetFolder.folderName || "Assessment",
            referenceId: residentId,
            title: "Assessment + Care Plan",
            fileName,
            mimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            existingDocuments: bundle.documents,
            existingObjectKey: outputKey,
        });
    };

    const generateAssessmentDocument = async (saveToBinder: boolean) => {
        if (!residentId || generatingAssessmentDocument) return;
        setGeneratingAssessmentDocument(true);
        try {
            await backendAPIStore.init();
            const response: any = await backendAPIStore.generateProviderAssessmentDocument(residentId, resolvedFormId || undefined);

            if (response?.success === false) {
                toast.current?.show({
                    severity: "warn",
                    summary: "Assessment not ready",
                    detail: response?.message || "Please save at least one assessment section before generating the document.",
                    life: 4500,
                });
                return;
            }

            const documentUrl = String(response?.documentUrl || "").trim();
            if (!documentUrl) {
                throw new Error(response?.message || "Assessment document could not be generated.");
            }

            if (saveToBinder) {
                await saveAssessmentDocumentToBinder(response);
                toast.current?.show({
                    severity: "success",
                    summary: "Added to Binder",
                    detail: "The Assessment + Care Plan document was saved to the resident binder.",
                    life: 3500,
                });
            } else {
                openUrlInNewTab(documentUrl);
                toast.current?.show({
                    severity: "success",
                    summary: "Download started",
                    detail: "The Assessment + Care Plan document is being downloaded.",
                    life: 3500,
                });
            }
        } catch (error: any) {
            toast.current?.show({
                severity: "error",
                summary: getErrorTitle(error),
                detail: error?.message || "Failed to generate assessment document.",
                life: 4000,
            });
        } finally {
            setGeneratingAssessmentDocument(false);
        }
    };

    const handleGenerateAssessmentDocument = () => {
        confirmDialog({
            message: renderConfirmMessage(
                "Assessment + Care Plan Document",
                "Choose whether to download the generated document or add it directly to the resident binder.",
            ),
            header: "Confirmation",
            icon: "pi pi-exclamation-triangle",
            rejectLabel: "Download",
            rejectClassName: "p-button-info p-button-outlined p-button-rounded",
            acceptLabel: "Add to Binder",
            acceptClassName: appConfirmDialogClasses.acceptClassName,
            accept: async () => {
                await generateAssessmentDocument(true);
            },
            reject: async () => {
                await generateAssessmentDocument(false);
            },
        });
    };

    const handleDownloadMarSheet = async () => {
        if (!residentId || !resolvedFormId || downloadingMarSheet) return;
        setDownloadingMarSheet(true);
        try {
            await backendAPIStore.init();
            const response: any = await backendAPIStore.generateProviderAssessmentMarSheet(residentId, resolvedFormId);
            if (response?.success === false) {
                toast.current?.show({ severity: "warn", summary: "MAR not ready", detail: response?.message || "Save the Medications step before downloading the MAR sheet.", life: 4500 });
                return;
            }
            const documentUrl = String(response?.documentUrl || "").trim();
            if (!documentUrl) throw new Error(response?.message || "MAR sheet could not be generated.");
            openUrlInNewTab(documentUrl);
            toast.current?.show({ severity: "success", summary: "Download started", detail: "The MAR sheet is being downloaded.", life: 3500 });
        } catch (error: any) {
            toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Failed to download MAR sheet.", life: 4000 });
        } finally {
            setDownloadingMarSheet(false);
        }
    };

    const profileFooterTemplate = () => (
        <div className="flex flex-wrap justify-content-end gap-2">
            <Button
                icon="pi pi-folder-open"
                outlined
                rounded
                label="Binder"
                severity="warning"
                size="small"
                onClick={() => navigate(residentBinderPath)}
            />
            {!isSharedResidentMode ? (
                <Button
                    icon="pi pi-file"
                    outlined
                    rounded
                    label="Documents"
                    severity="warning"
                    size="small"
                    onClick={() => navigate(RouterAddress.getResidentDocumentsPagePath(homeId || "", residentId || ""))}
                />
            ) : null}
        </div>
    );

    const generateAssessmentSection = () => {
        const topStatus = String(activeAssessmentForm?.status || "draft");
        const hasExistingAssessment = assessmentForms.length > 0;
        return (
            <div style={appNeutralPanelStyle} className="resident-careplan-actions">
                <div className="resident-careplan-actions-grid">
                    <div className="resident-careplan-actions-info">
                        <div className="grid mb-3">
                            <div className="col-12 lg:col-8">
                                <div style={{ fontWeight: 700, color: "#0f172a" }}>Manage Resident Assessment</div>
                                <div className="mt-3 flex flex-column gap-2" style={appMutedTextStyle}>
                                    <div className="m-0">
                                        Use resident source files such as Face sheet, MAR sheet, clinical records, assessment notes, and audio files to create the draft assessment and care plan.
                                    </div>
                                    <div className="flex flex-wrap gap-2 align-items-center mt-1">
                                        <Tag value={`VERSION ${activeAssessmentForm?.version || 1}`} severity="info" rounded />
                                        <Tag value={getAssessmentStatusLabel(topStatus).toUpperCase()} severity={getAssessmentStatusTagSeverity(topStatus)} rounded />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="resident-careplan-action-list">
                        <ul className="list-none m-0 p-0">
                            {[
                                {
                                    icon: "pi pi-bolt",
                                    label: "Start New Draft Automation",
                                    buttonLabel: "Start",
                                    severity: "warning" as const,
                                    disabled: false,
                                    loading: false,
                                    action: handleStartNewAssessment,
                                },
                                {
                                    icon: "pi pi-file-word",
                                    label: "Download Assessment",
                                    buttonLabel: "Download",
                                    severity: "secondary" as const,
                                    disabled: !resolvedFormId,
                                    loading: generatingAssessmentDocument,
                                    action: handleGenerateAssessmentDocument,
                                },
                                {
                                    icon: "pi pi-file",
                                    label: "Download MAR Sheet",
                                    buttonLabel: "Download",
                                    severity: "secondary" as const,
                                    disabled: !resolvedFormId,
                                    loading: downloadingMarSheet,
                                    action: handleDownloadMarSheet,
                                },
                                {
                                    icon: "pi pi-check-circle",
                                    label: "Mark Assessment Complete",
                                    buttonLabel: "Complete",
                                    severity: "secondary" as const,
                                    disabled: !resolvedFormId || String(topStatus || "").toLowerCase() === "completed",
                                    loading: false,
                                    action: handleMarkAssessmentComplete,
                                },
                                {
                                    icon: "pi pi-history",
                                    label: "Assessment History",
                                    buttonLabel: "Open",
                                    severity: "secondary" as const,
                                    disabled: !hasExistingAssessment,
                                    loading: false,
                                    action: () => setShowAssessmentHistoryDialog(true),
                                },
                            ].map((item, i) => (
                                <li
                                    key={i}
                                    className="flex flex-wrap align-items-center justify-content-between gap-3 py-3 px-3 resident-careplan-action-item"
                                >
                                    <span className={`flex align-items-center mr-2 gap-2${item.disabled ? " text-400" : ""}`} style={{ fontWeight: 600, color: "#0f172a" }}>
                                        <i className={`${item.icon} text-primary`} />
                                        <span>{item.label}</span>
                                    </span>
                                    <Button
                                        label={item.buttonLabel}
                                        severity={item.severity}
                                        rounded
                                        outlined
                                        size="small"
                                        disabled={item.disabled}
                                        loading={item.loading}
                                        onClick={item.action}
                                    />
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
                {resolvedFormId ? (
                    <div className="mt-3">
                        <NurseResidentAssessmentPage embedded />
                    </div>
                ) : null}
            </div>
        );
    };

    if (((!isSharedResidentMode && userRoleType !== ROLE_TYPE_PROVIDER) && !isAdminUser) || !residentId || !homeId) {
        return <Unauthorized />;
    }

    return (
        <div
            style={{ width: "100%", maxWidth: "100%", display: "flex", flexDirection: "column", gap: "1.25rem", margin: "0 auto", padding: "1.25rem" }}
            className="resident-details-page m-2 p-component"
        >
            <Toast ref={toast} />

            {loading ? (
                <div className="col-12 text-center d-flex justify-content-center align-items-center">
                    <ProgressSpinner role="status" style={{ color: "#185DA2" }} />
                </div>
            ) : (
                <div className="flex flex-column gap-3">
                    <div className="flex flex-wrap align-items-start justify-content-between gap-3">
                        <div style={{ flex: "1 1 0", minWidth: 0 }}>
                            <div style={dashboardPageContainerStyle}>
                                {renderDashboardHeader(
                                    `${residentProfile?.firstName || ""} ${residentProfile?.lastName || ""}`.trim()
                                        ? `${`${residentProfile?.firstName || ""} ${residentProfile?.lastName || ""}`.trim()} Assessment`
                                        : "Resident Assessment",
                                    "Manage assessment for private client",
                                )}
                            </div>
                        </div>
                        <div className="flex justify-content-end align-items-start">
                            {profileFooterTemplate()}
                        </div>
                    </div>

                    <div className="resident-careplan-layout">
                        {generateAssessmentSection()}
                    </div>
                </div>
            )}

            <Dialog
                visible={showFeatureAccessDialog}
                modal
                header={renderSurfaceHeader("Need More Access", "", "pi pi-wallet")}
                style={appDialogStyle}
                headerStyle={appDialogHeaderStyle}
                contentStyle={appDialogContentStyle}
                onHide={() => {
                    setShowFeatureAccessDialog(false);
                    setSubmitting(false);
                    setFeatureAccessDialogAllowUseCredit(false);
                    featureAccessDialogUseCreditActionRef.current = null;
                }}
            >
                <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                    <div style={{ color: "#596F80", lineHeight: 1.7 }}>
                        {featureAccessDialogMessage}
                    </div>
                    {!requiresProviderSubscriptionMessageOnly ? (
                        <>
                            <div style={{ background: "linear-gradient(180deg, #fffaf0 0%, #ffffff 100%)", border: "1px solid #fed7aa", borderRadius: 14, padding: "1rem" }}>
                                <div style={{ fontWeight: 700, color: "#9a3412" }}>Next</div>
                                <div className="mt-2" style={{ color: "#596F80", lineHeight: 1.7 }}>
                                    {featureAccessDialogAvailableCredits > 0
                                        ? `You currently have ${featureAccessDialogAvailableCredits} automation credit${featureAccessDialogAvailableCredits === 1 ? "" : "s"} available.`
                                        : "Buy Automation Credits to keep going."}
                                </div>
                            </div>
                            <div className="flex flex-wrap justify-content-end gap-2">
                                {featureAccessDialogAllowUseCredit ? (
                                    <Button
                                        type="button"
                                        label="Use 1 Credit"
                                        severity="warning"
                                        rounded
                                        outlined
                                        onClick={() => {
                                            const retryAction = featureAccessDialogUseCreditActionRef.current;
                                            setShowFeatureAccessDialog(false);
                                            setSubmitting(false);
                                            setFeatureAccessDialogAllowUseCredit(false);
                                            featureAccessDialogUseCreditActionRef.current = null;
                                            retryAction?.();
                                        }}
                                    />
                                ) : null}
                                {featureAccessDialogAvailableCredits <= 0 ? (
                                    <Button
                                        type="button"
                                        label="Buy Now"
                                        severity="warning"
                                        rounded
                                        onClick={() => {
                                            setShowFeatureAccessDialog(false);
                                            setSubmitting(false);
                                            navigate(`${RouterAddress.BILLING}?tab=one-time`);
                                        }}
                                    />
                                ) : null}
                            </div>
                        </>
                    ) : null}
                </div>
            </Dialog>

            <Dialog
                visible={showNewDraftDialog}
                modal
                header={renderSurfaceHeader("Start New Draft", "", "pi pi-upload")}
                style={startDraftDialogStyle}
                headerStyle={appDialogHeaderStyle}
                contentStyle={startDraftDialogContentStyle}
                onHide={() => {
                    if (submitting) return;
                    setShowNewDraftDialog(false);
                    setAutomationWizardStep("files");
                }}
            >
                <div className="flex flex-column gap-3">
                    {automationWizardStep === "choose" ? (
                        <>
                            <div style={{ ...appMutedTextStyle, maxWidth: "36rem" }}>
                                Choose the easiest way to start this resident&apos;s next assessment draft.
                            </div>
                            <div className="grid">
                                <div className="col-12">
                                    <div style={{ ...appSectionCardStyle, border: "1px solid #dbe7f3", borderRadius: 18, padding: "1rem", height: "100%", background: "linear-gradient(135deg, #f8fbff 0%, #ffffff 100%)" }}>
                                        <div style={{ fontWeight: 800, color: "#0f172a" }}>Use Source Files</div>
                                        <div className="mt-2" style={{ ...appMutedTextStyle, lineHeight: 1.7 }}>
                                            Upload source files like MAR sheets, past assessments, and clinical records. Label them, then let AFH Hub prepare a fresh draft.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            <div style={{ maxWidth: "36rem" }}>
                                <div style={{ fontWeight: 800, color: "#0f172a" }}>Build From Source Files</div>
                                <div className="mt-2" style={{ ...appMutedTextStyle, lineHeight: 1.7 }}>
                                    Add up to {MAX_STAGED_FILES} files first, such as MAR sheets, assessments, or clinical records. Then choose a label for each file before preparing the draft.
                                </div>
                                <div
                                    className="mt-3"
                                    style={{
                                        border: "1px solid #fdba74",
                                        background: "#fff7ed",
                                        borderRadius: 16,
                                        padding: "0.9rem 1rem",
                                        boxShadow: "0 10px 24px rgba(249, 115, 22, 0.08)",
                                    }}
                                >
                                    <div style={{ fontSize: "0.74rem", fontWeight: 800, color: "#9a3412", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                                        Important
                                    </div>
                                    <div className="mt-1" style={{ color: "#7c2d12", fontWeight: 700, lineHeight: 1.65 }}>
                                        Make sure all source documents are ready before you start. Each draft job creates a new version, so it is best to upload the complete set in one run.
                                    </div>
                                </div>
                            </div>

                            <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                hidden
                                accept={ALLOWED_EXTENSIONS.map((ext) => `.${ext}`).join(",")}
                                onChange={handleFileSelect}
                            />

                            <div className="flex flex-wrap align-items-center justify-content-between gap-3" style={{
                                ...appFormSectionStyle,
                                margin: 0,
                                padding: "0.9rem 1rem",
                                borderRadius: 18,
                                background: "#f8fafc",
                                boxShadow: "none",
                            }}>
                                <div>
                                    <div style={{ fontWeight: 700, color: "#0f172a" }}>Source Files</div>
                                    <div className="mt-1" style={appMutedTextStyle}>
                                        {stagedFiles.length === 0
                                            ? "Add source files, then label each one before preparing the draft."
                                            : `${stagedFiles.length} of ${MAX_STAGED_FILES} files ready for review.`}
                                    </div>
                                </div>
                                <div className="flex gap-2 flex-wrap">
                                    <Button
                                        type="button"
                                        label={`Add Files (${stagedFiles.length}/${MAX_STAGED_FILES})`}
                                        icon="pi pi-upload"
                                        severity="secondary"
                                        rounded
                                        outlined
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={submitting || stagedFiles.length >= MAX_STAGED_FILES}
                                    />
                                    <Button
                                        type="button"
                                        label="Prepare Draft"
                                        icon="pi pi-bolt"
                                        severity="warning"
                                        rounded
                                        onClick={() => { void startDraftAutomation(); }}
                                        disabled={submitting || stagedFiles.length === 0}
                                    />
                                    <Button
                                        type="button"
                                        label="Back"
                                        icon="pi pi-arrow-left"
                                        severity="secondary"
                                        rounded
                                        outlined
                                        onClick={() => {
                                            setShowNewDraftDialog(false);
                                            setAutomationWizardStep("files");
                                        }}
                                        disabled={submitting}
                                    />
                                </div>
                            </div>

                            {stagedFiles.length > 0 ? (
                                <div className="flex flex-column gap-3">
                                    {stagedFiles.map((staged) => (
                                        <div
                                            key={staged.id}
                                            style={{
                                                ...appFormSectionStyle,
                                                margin: 0,
                                                border: tagValidationRequested && !(staged.tags?.length) ? "1px solid #ef4444" : "1px solid #dbe7f3",
                                                borderRadius: 18,
                                                background: tagValidationRequested && !(staged.tags?.length) ? "#fef2f2" : "#ffffff",
                                                boxShadow: "0 8px 18px rgba(15, 23, 42, 0.04)",
                                            }}
                                        >
                                            <div className="flex flex-wrap justify-content-between align-items-start gap-2">
                                                <div>
                                                    <div style={{ fontWeight: 700, color: "#0f172a" }}>{staged.file.name}</div>
                                                    <div className="mt-1" style={appMutedTextStyle}>
                                                        {(staged.file.size / (1024 * 1024)).toFixed(1)} MB
                                                    </div>
                                                </div>
                                                <Button
                                                    type="button"
                                                    icon="pi pi-times"
                                                    text
                                                    rounded
                                                    severity="secondary"
                                                    onClick={() => removeStagedFile(staged.id)}
                                                />
                                            </div>
                                            <div className="mt-3">
                                                <div style={{ fontSize: "0.74rem", fontWeight: 800, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "0.55rem" }}>
                                                    File Labels
                                                </div>
                                                <div className="flex flex-wrap gap-2">
                                                    {DOC_TAG_OPTIONS.map((option) => {
                                                        const isSelected = (staged.tags || []).includes(option.value);
                                                        return (
                                                            <Button
                                                                key={`${staged.id}-${option.value}`}
                                                                type="button"
                                                                label={option.label}
                                                                size="small"
                                                                rounded
                                                                severity={isSelected ? "warning" : "secondary"}
                                                                outlined={!isSelected}
                                                                onClick={() => toggleStagedTag(staged.id, option.value)}
                                                            />
                                                        );
                                                    })}
                                                </div>
                                                {tagValidationRequested && !(staged.tags?.length) ? (
                                                    <div className="mt-2" style={{ color: "#b91c1c", fontSize: "0.82rem", fontWeight: 600 }}>
                                                        Choose at least one file label to continue.
                                                    </div>
                                                ) : null}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                null
                            )}
                        </>
                    )}
                </div>
            </Dialog>

            <Dialog
                visible={showAssessmentHistoryDialog}
                modal
                header={renderSurfaceHeader("Previous Assessment Versions", "", "pi pi-history")}
                style={appDialogStyle}
                headerStyle={appDialogHeaderStyle}
                contentStyle={appDialogContentStyle}
                onHide={() => setShowAssessmentHistoryDialog(false)}
            >
                {assessmentForms.length === 0 ? (
                    <div style={appMutedTextStyle}>No saved assessment versions found yet.</div>
                ) : (
                    <div className="flex flex-column gap-2">
                        {assessmentForms
                            .slice()
                            .sort((left: any, right: any) => getVersionSortTime(right) - getVersionSortTime(left))
                            .map((form: any, index: number) => {
                                const normalizedFormId = String(form?.formId || "").trim();
                                const isCurrent = normalizedFormId && normalizedFormId === resolvedFormId;
                                return (
                                    <div key={normalizedFormId || index} style={{ ...appSectionCardStyle, padding: "0.95rem", borderRadius: 18, border: "1px solid #e2e8f0" }}>
                                        <div className="flex justify-content-between align-items-start gap-3 flex-wrap">
                                            <div>
                                                <div style={{ fontWeight: 800, color: "#0f172a" }}>
                                                    Version V{form?.version || assessmentForms.length - index}
                                                </div>
                                                <div style={{ ...appMutedTextStyle, marginTop: "0.25rem" }}>
                                                    {formatVersionUpdatedAt(form)}
                                                </div>
                                            </div>
                                            <div className="flex align-items-center gap-2 flex-wrap justify-content-end">
                                                <Tag value={getAssessmentStatusLabel(form?.status).toUpperCase()} severity={getAssessmentStatusTagSeverity(form?.status)} />
                                                {isCurrent ? <Tag value="Current" severity="success" /> : null}
                                                <Button
                                                    type="button"
                                                    label={isCurrent ? "Open Current" : "Open"}
                                                    icon="pi pi-folder-open"
                                                    severity="secondary"
                                                    rounded
                                                    outlined
                                                    size="small"
                                                    onClick={() => {
                                                        setShowAssessmentHistoryDialog(false);
                                                        openAssessmentInline(normalizedFormId);
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                    </div>
                )}
            </Dialog>
        </div>
    );
};

export default ProviderResidentAssessmentPage;
